// importa lo scss del blocco: cosi' Vite genera anche il file css
// e lo aggiunge al manifest, che e' quello che il tema legge per
// fare l'enqueue automatico (vedi extensions/acf/acf.php)
import './_timeline.scss';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// necessario per poter usare ScrollTrigger.create(): senza questa riga
// gsap non "conosce" il plugin e le trigger non farebbero nulla
gsap.registerPlugin(ScrollTrigger);

/**
 * Inizializza l'animazione della timeline allo scroll.
 * Le transizioni visive (dot, accordion del testo) sono gestite
 * interamente via CSS attraverso la classe `.is-active`.
 *
 * A differenza di una prima versione basata su onEnter/onEnterBack
 * per singolo step (eventi "a soglia", quindi persi con scroll veloce
 * o zone che si sovrappongono), qui lo step attivo viene RICALCOLATO
 * DA ZERO ad ogni aggiornamento dello scroll, guardando la posizione
 * reale di tutti gli step rispetto a una linea di riferimento nel
 * viewport. Essendo un calcolo stateless (non dipende da "quale evento
 * e' scattato per ultimo"), non puo' saltare nessuno step, in nessuna
 * direzione o velocita' di scroll.
 */
function initTimeline() {
    // seleziona tutti i blocchi timeline presenti nella pagina
    const timelines = document.querySelectorAll('[data-timeline]');

    if (!timelines.length) {
        return;
    }

    // ripete la logica per ogni blocco timeline (stato attivo indipendente per ognuno)
    timelines.forEach((timeline) => {
        const steps = Array.from(timeline.querySelectorAll('[data-timeline-step]'));

        if (!steps.length) {
            return;
        }

        // FUNZIONE CHIAVE per "un solo step aperto alla volta":
        // riceve lo step che deve diventare attivo, scorre TUTTI gli step
        // e aggiunge is-active solo a quello passato, rimuovendola da tutti gli altri
        const setActiveStep = (activeStep) => {
            steps.forEach((step) => {
                step.classList.toggle('is-active', step === activeStep);
            });
        };

        // FUNZIONE CHIAVE per la robustezza: ricalcola da capo quale step
        // dovrebbe essere attivo, indipendentemente da cosa era attivo prima
        const updateActiveStep = () => {
            // linea di riferimento nel viewport: il 65% dell'altezza schermo
            // (stessa soglia "top 65%" usata prima, ma qui e' un calcolo
            // esplicito invece che una start/end di ScrollTrigger)
            const threshold = window.innerHeight * 0.65;

            let current = null;

            // scorre gli step in ordine (dal primo all'ultimo) e tiene
            // traccia dell'ULTIMO che ha gia' superato la linea di riferimento
            // (cioe' il suo bordo superiore e' sopra la soglia): questo
            // corrisponde naturalmente allo step "attuale" durante la lettura,
            // sia scendendo che risalendo
            steps.forEach((step) => {
                const rect = step.getBoundingClientRect();
                if (rect.top <= threshold) {
                    current = step;
                }
            });

            // se current e' null significa che non abbiamo ancora raggiunto
            // il primo step (siamo sopra tutta la timeline): nessuno step attivo
            if (current) {
                setActiveStep(current);
            } else {
                steps.forEach((step) => step.classList.remove('is-active'));
            }
        };

        // un solo ScrollTrigger per l'intero blocco (non uno per step):
        // serve solo a dire "in questo range di scroll, richiama
        // updateActiveStep() ad ogni variazione", il calcolo vero e proprio
        // lo fa la funzione sopra
        ScrollTrigger.create({
            trigger: timeline,
            start: 'top bottom',   // appena il blocco entra nella pagina dal basso
            end: 'bottom top',     // finche' non esce completamente dall'alto
            onUpdate: updateActiveStep,
            onEnter: updateActiveStep,
            onEnterBack: updateActiveStep,
        });
    });
}

// GUARDIA ANTI-RACE-CONDITION:
// se lo script viene eseguito DOPO che il browser ha gia' finito di
// costruire il DOM (script con defer, o caricato in coda), l'evento
// 'DOMContentLoaded' e' gia' scattato in passato e non scattera' mai piu'.
// Controlliamo readyState: se il documento sta ancora caricando ci
// mettiamo in ascolto, altrimenti chiamiamo la funzione subito.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTimeline);
} else {
    initTimeline();
}

// export utile per test manuali in console o per un eventuale re-init
// futuro (es. dopo un caricamento AJAX di contenuti)
export default initTimeline;